import { useState, useEffect } from 'react';
import { ws } from '../lib/websocket';
import { api } from '../lib/api';

interface Vehicle {
  id: number;
  name: string;
  type: string;
  current_location_id: number;
  passenger_count: number;
  location_name?: string;
  speed?: number;
}

interface NPCCounts {
  facilities: Record<number, number>;
  projects: Record<number, number>;
}

export function useSimulation() {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [npcCounts, setNpcCounts] = useState<NPCCounts>({ facilities: {}, projects: {} });
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Connect to WebSocket
    ws.connect();

    // Listen for connection status
    const unsubConnection = ws.on('connection', ({ connected }: { connected: boolean }) => {
      setIsConnected(connected);
    });

    // Listen for vehicle updates
    const unsubVehicle = ws.on('vehicle', (data: unknown) => {
      const vehicleData = data as Record<string, unknown>;
      setVehicles(prev => {
        const index = prev.findIndex(v => v.id === vehicleData.id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = { ...updated[index], ...vehicleData } as Vehicle;
          return updated;
        }
        return prev;
      });
    });

    // Listen for NPC updates
    const unsubNPC = ws.on('npc', (data: unknown) => {
      const npcData = data as Record<string, unknown>;
      setNpcCounts(prev => ({
        ...prev,
        facilities: {
          ...prev.facilities,
          [npcData.locationId as number]: npcData.workersCount as number
        }
      }));
    });

    // Listen for tick updates (batch updates)
    const unsubTick = ws.on('tick', (data: unknown) => {
      const tickData = data as Record<string, unknown>;
      if (tickData.vehicles) {
        setVehicles(tickData.vehicles as Vehicle[]);
      }
    });

    // Initial load from REST API
    api.getVehicles().then((response: unknown) => {
      const resp = response as Record<string, unknown>;
      if (resp.vehicles) {
        setVehicles(resp.vehicles as Vehicle[]);
      }
    });

    return () => {
      unsubConnection();
      unsubVehicle();
      unsubNPC();
      unsubTick();
      ws.disconnect();
    };
  }, []);

  return { vehicles, npcCounts, isConnected };
}
